import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
  type ReactNode,
} from 'react'

import { clsx } from 'clsx'

/**
 * Toast variant types
 */
export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

/**
 * Toast data structure
 */
export interface Toast {
  id: string
  variant: ToastVariant
  message: string
  duration?: number
}

/**
 * Toast context value
 */
interface ToastContextValue {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, 'id'>) => void
  removeToast: (id: string) => void
  success: (message: string, duration?: number) => void
  error: (message: string, duration?: number) => void
  warning: (message: string, duration?: number) => void
  info: (message: string, duration?: number) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

/**
 * Hook to access toast functionality
 *
 * @example
 * const toast = useToast()
 * toast.success('Entry saved successfully')
 * toast.error('Failed to save entry. Please try again.')
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

// ============================================================================
// Variant styles & icons
// ============================================================================

export const variantStyles: Record<ToastVariant, string> = {
  success: clsx('bg-green-50 dark:bg-green-900/50', 'border-green-200 dark:border-green-800', 'text-green-800 dark:text-green-200'),
  error: clsx('bg-red-50 dark:bg-red-900/50', 'border-red-200 dark:border-red-800', 'text-red-800 dark:text-red-200'),
  warning: clsx('bg-amber-50 dark:bg-amber-900/50', 'border-amber-200 dark:border-amber-800', 'text-amber-800 dark:text-amber-200'),
  info: clsx('bg-muted', 'border-border', 'text-foreground'),
}

export const variantIcons: Record<ToastVariant, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
}

// ============================================================================
// Toast Provider
// ============================================================================

export interface ToastProviderProps {
  children: ReactNode
  /** Maximum number of toasts to show at once */
  maxToasts?: number
  /** Default duration in milliseconds */
  defaultDuration?: number
}

/**
 * Toast provider component
 *
 * Wrap your app with this provider to enable toast notifications.
 *
 * @example
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 */
export function ToastProvider({
  children,
  maxToasts = 5,
  defaultDuration = 5000,
}: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const newToast: Toast = { ...toast, id, duration: toast.duration ?? defaultDuration }

    setToasts((prev) => {
      const updated = [...prev, newToast]
      return updated.length > maxToasts ? updated.slice(-maxToasts) : updated
    })
  }, [defaultDuration, maxToasts])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const success = useCallback((message: string, duration?: number) => {
    addToast({ variant: 'success', message, ...(duration !== undefined ? { duration } : {}) })
  }, [addToast])

  const error = useCallback((message: string, duration?: number) => {
    addToast({ variant: 'error', message, ...(duration !== undefined ? { duration } : {}) })
  }, [addToast])

  const warning = useCallback((message: string, duration?: number) => {
    addToast({ variant: 'warning', message, ...(duration !== undefined ? { duration } : {}) })
  }, [addToast])

  const info = useCallback((message: string, duration?: number) => {
    addToast({ variant: 'info', message, ...(duration !== undefined ? { duration } : {}) })
  }, [addToast])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, warning, info }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  )
}

// ============================================================================
// Toast Container
// ============================================================================

function ToastContainer(): JSX.Element {
  const { toasts } = useToast()

  return (
    <div
      aria-label="Notifications"
      aria-live="polite"
      className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-md w-full pointer-events-none"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  )
}

// ============================================================================
// Toast Item
// ============================================================================

interface ToastItemProps {
  toast: Toast
}

function useToastTimers(toast: Toast, onRemove: (id: string) => void): {
  isExiting: boolean
  handleDismiss: () => void
} {
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const exitTimer = setTimeout(() => setIsExiting(true), toast.duration - 300)
      const removeTimer = setTimeout(() => onRemove(toast.id), toast.duration)
      return () => { clearTimeout(exitTimer); clearTimeout(removeTimer) }
    }
  }, [toast.id, toast.duration, onRemove])

  const handleDismiss = (): void => {
    setIsExiting(true)
    setTimeout(() => onRemove(toast.id), 300)
  }

  return { isExiting, handleDismiss }
}

function ToastItem({ toast }: ToastItemProps): JSX.Element {
  const { removeToast } = useToast()
  const { isExiting, handleDismiss } = useToastTimers(toast, removeToast)

  return (
    <div
      className={clsx(
        'flex items-start gap-3 p-4 rounded-lg border shadow-lg',
        'pointer-events-auto',
        'transition-all duration-300',
        isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0 animate-slide-in',
        variantStyles[toast.variant]
      )}
      role="alert"
    >
      <span aria-hidden="true" className="flex-shrink-0 w-5 h-5 flex items-center justify-center font-bold">
        {variantIcons[toast.variant]}
      </span>

      <p className="flex-1 text-sm font-medium">{toast.message}</p>

      <button
        aria-label="Dismiss notification"
        className={clsx('flex-shrink-0 p-1 rounded', 'hover:bg-black/10 dark:hover:bg-white/10', 'transition-colors duration-150')}
        onClick={handleDismiss}
      >
        <svg aria-hidden="true" className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      </button>
    </div>
  )
}

// ============================================================================
// CSS for animation (add to your global CSS or index.css)
// ============================================================================

/*
Add to index.css:

@keyframes slide-in {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.animate-slide-in {
  animation: slide-in 0.3s ease-out;
}
*/

export default ToastProvider
